import { rootMargin } from '@helpers/animate';
import { API_URLS } from '@helpers/api';
import { InterfaceIcon } from '@helpers/icons';
import { Device, until } from '@helpers/media';
import { withMotion } from '@hoc/withMotion';
import { withQueryParams } from '@hoc/withQueryParams';
import { useApi } from '@hooks/useApi';
import { useInView } from '@hooks/useInView';
import Button from '@stories/Components/Buttons/Button/Button';
import BrandCard from '@stories/Components/Cards/BrandCard/BrandCard';
import Filters from '@stories/Components/Listings/Filters/Filters';
import LoadingIndicator from '@stories/Components/Misc/LoadingIndicator/LoadingIndicator';
import isEqual from 'lodash/isEqual';
import qs from 'query-string';
import * as React from 'react';
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault, } from 'use-query-params';
import S from './BrandCardListing.styles';
const BrandCardListing = (props) => {
    const [ref, inView] = useInView({ rootMargin }, false, props.pageContext?.isEditMode);
    const imageSizes = `${until(Device.Tablet)} 50vw, ${until(Device.DesktopSmall)} 25vw, ${until(Device.DesktopLarge)} 17vw, 260px`;
    const [params, setParams] = useQueryParams(Object.assign({
        pageIndex: withDefault(NumberParam, 1),
        pageSize: withDefault(NumberParam, 48),
        sortBy: withDefault(StringParam, ''),
        searchTerm: withDefault(StringParam, ''),
    }, ...props.initialData.filters.map((f) => ({
        [f.name]: withDefault(ArrayParam, []),
    }))));
    const api = useApi({
        initialData: props.initialData,
        url: `${API_URLS.brandCardsListing}?${qs.stringify({
            ...params,
            categoryGroups: props.categoryGroups,
            cultureCode: props.cultureCode,
            docId: props.pageContext?.documentId,
            docIds: props.docIds,
            engagementType: props.engagementType,
        })}`,
    });
    const [cards, setCards] = React.useState(props.initialData.items);
    const [paramsCache, setParamsCache] = React.useState(undefined);
    React.useEffect(() => {
        if (api.data && api.status === 'complete') {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { pageIndex, ...rest } = params;
            // If the params (minus pageIndex) haven't changed, append the new results to the existing ones
            if (isEqual(rest, paramsCache)) {
                setCards([...cards, ...api.data.items]);
            }
            else {
                setCards(api.data.items);
                setParamsCache(rest);
            }
        }
    }, [api.status]);
    // Cache params (minus page index) to ensure we don't render old results
    React.useEffect(() => {
        if (paramsCache) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { pageIndex, ...rest } = params;
        setParamsCache(rest);
    }, [params]);
    const filters = React.useMemo(() => {
        return api.data?.filters
            .filter((f) => f.options.length > 0)
            .map((f) => ({
            ...f,
            value: params[f.name],
            onChange: (value) => {
                if (value === '') {
                    setParams({ [f.name]: [value], pageIndex: 1 }, 'pushIn');
                }
                else {
                    const values = params[f.name];
                    if (values.includes(value)) {
                        setParams({ [f.name]: values.filter((v) => v !== value), pageIndex: 1 }, 'pushIn');
                    }
                    else {
                        setParams({ [f.name]: [...values, value], pageIndex: 1 }, 'pushIn');
                    }
                }
            },
            hideMobile: false,
            type: 'multi',
        }));
    }, [api.data?.filters]);
    return (React.createElement(S.Container, { id: props.anchorId, ref: ref, flush: "top", translations: props.translations, "data-animate": inView },
        React.createElement(Filters, { filters: filters, sortBy: props.sortBy
                ? {
                    ...props.sortBy,
                    value: params.sortBy,
                    onChange: (value) => setParams({ sortBy: value, pageIndex: 1 }, 'pushIn'),
                }
                : undefined, clearFilters: () => setParams({
                searchTerm: undefined,
                pageSize: params.pageSize,
                sortBy: params.sortBy,
                pageIndex: 1,
            }, 'replace'), searchInput: props.searchInput
                ? {
                    ...props.searchInput,
                    onSubmit: (value) => setParams({ searchTerm: value, pageIndex: 1 }, 'pushIn'),
                    value: params.searchTerm,
                }
                : undefined, translations: props.translations }),
        React.createElement(S.ResultsGrid, null, cards.length > 0 ? (React.createElement(React.Fragment, null, cards.map((item) => (React.createElement(BrandCard, { key: item.id, ...item, headingType: "h3", sizes: imageSizes, imageOnly: props.imageOnlyCards }))))) : (React.createElement(S.NoResults, null, props.translations?.['global.listings.noresults']))),
        api?.data && cards.length > 0 ? (React.createElement(React.Fragment, null, api.data?.totalItems > cards.length && (React.createElement(Button, { buttonType: "tertiary", onClick: () => setParams({ pageIndex: params.pageIndex + 1 }, 'pushIn'), icon: api.status === 'loading' ? InterfaceIcon.Loader : InterfaceIcon.ArrowDown }, props.translations?.['global.listings.loadmore'])))) : null,
        api.status === 'loading' && (React.createElement(LoadingIndicator, { label: props.translations?.['global.listings.loading'], layout: "fixed" }))));
};
export default withQueryParams(withMotion(BrandCardListing));
